import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

import { callClaude } from '../claude'

function OpportunityCompetitors({ opportunity, profile, session, onClose }) {
  const [competitors, setCompetitors] = useState([])
  const [allCompetitors, setAllCompetitors] = useState([])
  const [selected, setSelected] = useState(null)
  const [editingOverlay, setEditingOverlay] = useState(null)
  const [overlayForm, setOverlayForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [generatingId, setGeneratingId] = useState(null)
  const [loading, setLoading] = useState(true)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const [{ data: linked }, { data: all }] = await Promise.all([
      supabase.from('competitor_opportunities')
        .select('*, competitors(*)')
        .eq('opportunity_id', opportunity.id),
      supabase.from('competitors')
        .select('*')
        .eq('organisation_id', profile?.organisation_id)
        .eq('is_active', true)
        .order('name')
    ])
    setCompetitors(linked || [])
    setAllCompetitors(all || [])
    setLoading(false)
  }

  const handleAdd = async (competitorId) => {
    const { data, error } = await supabase.from('competitor_opportunities')
      .insert([{ competitor_id: competitorId, opportunity_id: opportunity.id, organisation_id: profile?.organisation_id }])
      .select('*, competitors(*)')
      .single()
    if (!error && data) setCompetitors(prev => [...prev, data])
  }

  const handleRemove = async (id) => {
    await supabase.from('competitor_opportunities').delete().eq('id', id)
    setCompetitors(prev => prev.filter(c => c.id !== id))
    if (selected?.id === id) setSelected(null)
  }

  const handleEditOverlay = (co) => {
    setOverlayForm({
      relationship_with_buyer: co.relationship_with_buyer || '',
      confirmed_in_field: co.confirmed_in_field || false,
      likely_approach: co.likely_approach || '',
      estimated_pwin: co.estimated_pwin || '',
      relevant_recent_wins: co.relevant_recent_wins || '',
      bid_notes: co.bid_notes || ''
    })
    setEditingOverlay(co)
  }

  const handleSaveOverlay = async () => {
    setSaving(true)
    const payload = {
      relationship_with_buyer: overlayForm.relationship_with_buyer || null,
      confirmed_in_field: overlayForm.confirmed_in_field || false,
      likely_approach: overlayForm.likely_approach || null,
      estimated_pwin: overlayForm.estimated_pwin ? parseInt(overlayForm.estimated_pwin) : null,
      relevant_recent_wins: overlayForm.relevant_recent_wins || null,
      bid_notes: overlayForm.bid_notes || null
    }
    const { data } = await supabase.from('competitor_opportunities').update(payload).eq('id', editingOverlay.id).select('*, competitors(*)').single()
    if (data) {
      setCompetitors(prev => prev.map(c => c.id === editingOverlay.id ? data : c))
      if (selected?.id === editingOverlay.id) setSelected(data)
    }
    setSaving(false)
    setEditingOverlay(null)
  }

  const handleGenerateGhostStrategy = async (co) => {
    setGeneratingId(co.id)
    try {
      const system = `You are an expert bid strategist specialising in Government and Defence procurement. Generate a structured ghost strategy for a specific competitor on a specific bid. Be commercially precise and actionable. Respond in JSON only.`

      const prompt = `Generate a ghost strategy for this competitor on this specific bid.

Opportunity: ${opportunity.title}
Buyer: ${opportunity.buyer}
${opportunity.sector ? 'Sector: ' + opportunity.sector : ''}

Competitor: ${co.competitors?.name}
${co.competitors?.size ? 'Size: ' + co.competitors.size : ''}
${co.competitors?.sector_focus ? 'Sector Focus: ' + co.competitors.sector_focus : ''}
${co.competitors?.strengths ? 'Known Strengths: ' + co.competitors.strengths : ''}
${co.competitors?.weaknesses ? 'Known Weaknesses: ' + co.competitors.weaknesses : ''}
${co.competitors?.typical_pricing ? 'Typical Pricing: ' + co.competitors.typical_pricing : ''}
${co.competitors?.ghost_strategy ? 'Historical Ghost Strategy: ' + co.competitors.ghost_strategy : ''}
${co.relationship_with_buyer ? 'Relationship with Buyer: ' + co.relationship_with_buyer : ''}
${co.likely_approach ? 'Likely Approach on this bid: ' + co.likely_approach : ''}
${co.confirmed_in_field ? 'Confirmed in field: Yes' : ''}

Respond with a JSON object:
{
  "likely_win_themes": ["theme 1", "theme 2", "theme 3"],
  "likely_pricing_strategy": "How they will likely price this bid",
  "key_vulnerabilities": ["vulnerability 1", "vulnerability 2"],
  "counter_strategy": "2-3 sentences on how to position against them",
  "ghost_actions": ["specific action 1", "specific action 2", "specific action 3"],
  "risk_level": "High" | "Medium" | "Low"
}`

      const result = await callClaude({ system, prompt, max_tokens: 800 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('No JSON in response')
      const parsed = JSON.parse(jsonMatch[0])

      const { data } = await supabase
        .from('competitor_opportunities')
        .update({ ghost_strategy_ai: parsed })
        .eq('id', co.id)
        .select('*, competitors(*)')
        .single()

      if (data) setCompetitors(prev => prev.map(c => c.id === co.id ? data : c))

    } catch (err) {
      console.error('Ghost strategy error:', err)
    }
    setGeneratingId(null)
  }

  const unlinked = allCompetitors.filter(c => !competitors.find(co => co.competitor_id === c.id))

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>Competitors</div>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)' }}>{opportunity.title}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>{opportunity.buyer}</div>
      </div>

      {/* Add competitor */}
      {unlinked.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card__header"><span className="card__title">Add Competitor to this Bid</span></div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {unlinked.map(c => (
              <button key={c.id} onClick={() => handleAdd(c.id)}
                style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '5px 14px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--white)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--green)' }}
              >+ {c.name}</button>
            ))}
          </div>
        </div>
      )}

      {loading && <p style={{ color: 'var(--slate)' }}>Loading...</p>}

      {!loading && competitors.length === 0 && (
        <div className="card"><p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>No competitors tagged to this bid yet.</p></div>
      )}

      {competitors.map(co => {
        const isSelected = selected?.id === co.id
        return (
          <div key={co.id} style={{ background: 'var(--white)', border: '1px solid var(--green-light)', borderLeft: '4px solid var(--green)', marginBottom: '12px' }}>
            <div style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setSelected(isSelected ? null : co)}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--green-dark)', fontFamily: 'Cormorant Garamond, serif' }}>{co.competitors?.name}</div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
                  {co.confirmed_in_field && <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--white)', background: 'var(--red)', padding: '2px 8px', letterSpacing: '0.06em' }}>CONFIRMED IN FIELD</span>}
                  {co.relationship_with_buyer && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>Buyer relationship: {co.relationship_with_buyer}</span>}
                  {co.estimated_pwin && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>Est. PWin: {co.estimated_pwin}%</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{isSelected ? '▲' : '▼'}</span>
              </div>
            </div>

            {isSelected && (
              <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--green-light)' }}>
                {editingOverlay?.id === co.id ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                    <div className="form-group">
                      <label>Relationship with Buyer</label>
                      <input type="text" value={overlayForm.relationship_with_buyer} onChange={e => setOverlayForm({ ...overlayForm, relationship_with_buyer: e.target.value })} placeholder="e.g. Incumbent, Preferred, Neutral" />
                    </div>
                    <div className="form-group">
                      <label>Estimated PWin (%)</label>
                      <input type="number" value={overlayForm.estimated_pwin} onChange={e => setOverlayForm({ ...overlayForm, estimated_pwin: e.target.value })} placeholder="0-100" min="0" max="100" />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Likely Approach on this Bid</label>
                      <textarea value={overlayForm.likely_approach} onChange={e => setOverlayForm({ ...overlayForm, likely_approach: e.target.value })} rows={2} placeholder="How are they likely to position on this specific procurement?" />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Relevant Recent Wins</label>
                      <textarea value={overlayForm.relevant_recent_wins} onChange={e => setOverlayForm({ ...overlayForm, relevant_recent_wins: e.target.value })} rows={2} placeholder="Recent wins with this buyer or in this sector" />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Bid Notes</label>
                      <textarea value={overlayForm.bid_notes} onChange={e => setOverlayForm({ ...overlayForm, bid_notes: e.target.value })} rows={2} placeholder="Any other intelligence specific to this bid" />
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input type="checkbox" id={"confirmed-" + co.id} checked={overlayForm.confirmed_in_field} onChange={e => setOverlayForm({ ...overlayForm, confirmed_in_field: e.target.checked })} style={{ width: 16, height: 16 }} />
                      <label htmlFor={"confirmed-" + co.id} style={{ cursor: 'pointer', marginBottom: 0, fontWeight: 600 }}>Confirmed in field</label>
                    </div>
                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px' }}>
                      <button className="btn btn--primary" onClick={handleSaveOverlay} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                      <button className="btn btn--secondary" onClick={() => setEditingOverlay(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      {co.competitors?.sector_focus && <div><div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Sector Focus</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.competitors.sector_focus}</div></div>}
                      {co.competitors?.typical_pricing && <div><div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Pricing Approach</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.competitors.typical_pricing}</div></div>}
                      {co.competitors?.strengths && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Strengths</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.competitors.strengths}</div></div>}
                      {co.competitors?.weaknesses && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Weaknesses</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.competitors.weaknesses}</div></div>}
                      {co.likely_approach && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.625rem', fontWeight: 700, color: '#E67E22', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Likely Approach on this Bid</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.likely_approach}</div></div>}
                      {co.relevant_recent_wins && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.625rem', fontWeight: 700, color: '#E67E22', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Relevant Recent Wins</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{co.relevant_recent_wins}</div></div>}
                      {co.bid_notes && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '0.625rem', fontWeight: 700, color: '#E67E22', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Bid Notes</div><div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', fontStyle: 'italic' }}>{co.bid_notes}</div></div>}
                    </div>
                    {co.ghost_strategy_ai && (
                      <div style={{ background: 'var(--green-dark)', padding: '16px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>AI Ghost Strategy</div>
                          <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 8px', background: co.ghost_strategy_ai.risk_level === 'High' ? 'var(--red)' : co.ghost_strategy_ai.risk_level === 'Medium' ? '#E67E22' : 'var(--green)', color: 'var(--white)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {co.ghost_strategy_ai.risk_level} Threat
                          </span>
                        </div>
                        {co.ghost_strategy_ai.likely_win_themes && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Their Likely Win Themes</div>
                            {co.ghost_strategy_ai.likely_win_themes.map((t, i) => (
                              <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--white)', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>• {t}</div>
                            ))}
                          </div>
                        )}
                        {co.ghost_strategy_ai.likely_pricing_strategy && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Likely Pricing Strategy</div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--white)', lineHeight: 1.5 }}>{co.ghost_strategy_ai.likely_pricing_strategy}</div>
                          </div>
                        )}
                        {co.ghost_strategy_ai.key_vulnerabilities && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Key Vulnerabilities</div>
                            {co.ghost_strategy_ai.key_vulnerabilities.map((v, i) => (
                              <div key={i} style={{ fontSize: '0.8125rem', color: '#E67E22', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>• {v}</div>
                            ))}
                          </div>
                        )}
                        {co.ghost_strategy_ai.counter_strategy && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Counter Strategy</div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.5, fontWeight: 500 }}>{co.ghost_strategy_ai.counter_strategy}</div>
                          </div>
                        )}
                        {co.ghost_strategy_ai.ghost_actions && (
                          <div>
                            <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Recommended Actions</div>
                            {co.ghost_strategy_ai.ghost_actions.map((a, i) => (
                              <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--white)', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '8px' }}>
                                <span style={{ color: 'var(--green-light)', flexShrink: 0 }}>{i + 1}.</span>{a}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid var(--green-light)' }}>
                      <button onClick={() => handleEditOverlay(co)} style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Edit Bid Intelligence</button>
                  {profile?.organisations?.ai_enabled && (
                    <button
                      onClick={() => handleGenerateGhostStrategy(co)}
                      disabled={generatingId === co.id}
                      style={{ background: generatingId === co.id ? 'var(--green-pale)' : 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', opacity: generatingId === co.id ? 0.7 : 1 }}
                    >
                      {generatingId === co.id ? '⏳ Generating...' : '✦ Ghost Strategy'}
                    </button>
                  )}
                      <button onClick={() => handleRemove(co.id)} style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer', marginLeft: 'auto' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                      >Remove</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      <div className="actions" style={{ marginTop: '16px' }}>
        <button className="btn btn--secondary" onClick={onClose}>← Back to Pipeline</button>
      </div>
    </div>
  )
}

export default OpportunityCompetitors